import { Fragment, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Equal, Loader, Plus } from "lucide-react";
import { Badge } from "@ui/Badge";
import { Card } from "@ui/Card";
import type { Calculation, DriverName } from "@/types/Calculation";
import { useMultiStepForm } from "@/hooks";
import { formatNumber } from "@/utils/formatter";
import { cn } from "@/utils/tailwind";

interface CalculationCardProps {
  driverName: DriverName;
  isLoading?: boolean;
  data?: Calculation;
  highlight?: boolean;
}

export const CalculationCard: FC<CalculationCardProps> = ({
  driverName,
  isLoading,
  data,
  highlight,
}) => {
  const { t } = useTranslation();
  const { form } = useMultiStepForm();
  const hasPvInstallation = form.watch("step4.hasPvInstallation") === "true";

  const prepKey = "from_before_consonant";

  const getDriverLabel = (): string => {
    if (driverName === "autoconsumption") {
      return hasPvInstallation
        ? t("step6.driverLabels.autoconsumptionMaximizingAutoconsumption")
        : t("step6.driverLabels.autoconsumptionMinimizingPowerFee");
    }
    return t(`step6.driverLabels.${driverName}`);
  };

  const texts = {
    label: getDriverLabel(),
    best: t("step6.bestSavings"),
    title: `${t("step6.estimatedSavings")} ${t(`step6.prepositions.${prepKey}`)}`,
    loading: t("loading"),
    distribution: t("step6.savingsDistribution"),
    purchase: t("step6.savingsPurchase"),
    feeSavings: t("step6.feeSavings"),
    total: t("step6.savingsTotal"),
    currency: t("zl"),
  };

  const cardData = [
    { label: texts.distribution, value: formatNumber(data?.distribution_savings) },
    { label: texts.purchase, value: formatNumber(data?.energy_purchase_savings) },
    { label: texts.feeSavings, value: formatNumber(data?.power_fee_savings) },
    { label: texts.total, value: formatNumber(data?.total_savings), bold: true },
  ];

  return (
    <Card
      className={cn(
        "relative gap-0 border p-4 md:px-4 md:py-6 lg:px-4 lg:py-6",
        highlight && "border-primary bg-primary/20 border-2 py-6",
      )}
      data-test={`calculation-card-${driverName}`}
    >
      {highlight && (
        <Badge
          className="absolute -top-3 left-4"
          data-test={`calculation-card-${driverName}-badge`}
        >
          {texts.best}
        </Badge>
      )}

      <h3 className="mb-4 lg:mb-6 lg:text-xl" data-test={`calculation-card-${driverName}-heading`}>
        {texts.title} <span className="font-bold">{texts.label}.</span>
      </h3>

      {isLoading ? (
        <div className="flex items-center justify-center gap-1">
          <Loader className="size-4 animate-spin" />
          <span className="text-xs font-extralight">{texts.loading}</span>
        </div>
      ) : (
        data && (
          <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
            {cardData.map(({ label, value, bold }, i) => {
              const isLastItem = i === cardData.length - 1;
              const Operator = isLastItem ? Equal : Plus;
              return (
                <Fragment key={label}>
                  {i > 0 && <Operator className="size-4 lg:size-6" strokeWidth={1.5} />}
                  <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center sm:flex-row">
                    <div>
                      <p className="text-xs font-extralight">{label}</p>
                      <p
                        className={cn(
                          "lg:text-base xl:text-lg xl:whitespace-nowrap",
                          bold && "font-semibold",
                          highlight && bold && "text-primary lg:text-foreground",
                        )}
                        data-test={bold ? `calculation-card-${driverName}-total` : undefined}
                      >
                        {value} {texts.currency}
                      </p>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        )
      )}
    </Card>
  );
};
